// /src/components/Login.js

import React, { useState, useTransition } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../api/axios';
import { 
  FaSpinner, 
  FaLock, 
  FaUser,
  FaExclamationCircle 
} from 'react-icons/fa';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';

const Login = ({ setIsAuthenticated, setUserRole }) => {
  const { t } = useTranslation();
  const [isPending, startTransition] = useTransition();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
  
    try {
      const response = await api.post('/auth/login', { 
        username, 
        password 
      });
      
      // First check if user is verified
      if (response.data.error === 'EMAIL_NOT_VERIFIED' || !response.data.isVerified) {
        localStorage.setItem('userEmail', response.data.email);
        // Don't set any auth tokens or state
        navigate('/verify', { 
          state: { email: response.data.email }
        });
        return;
      }
  
      // Only set auth data if user is verified
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('role', response.data.role);
      localStorage.setItem('userEmail', response.data.email);
      localStorage.setItem('userId', response.data.userId);
      
      startTransition(() => {
        setIsAuthenticated(true);
        setUserRole(response.data.role);
        navigate('/dashboard');
      });
    } catch (error) {
      if (error.response?.data?.error === 'EMAIL_NOT_VERIFIED') {
        localStorage.setItem('userEmail', error.response.data.email);
        // Clear any existing auth data
        localStorage.removeItem('token');
        localStorage.removeItem('role');
        setIsAuthenticated(false);
        setUserRole(null);
        
        navigate('/verify', { 
          state: { email: error.response.data.email }
        });
      } else {
        setError(
          error.response?.data?.error || t('auth.login.errors.generalError')
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-900 px-4">
      <div className="w-full max-w-md space-y-8">
        {/* Logo and Title */}
        <div className="text-center">
          <Link to="/">
            <h1 className="text-4xl font-bold text-white mb-2 hover:text-gray-200 transition-colors">
              {t('auth.common.brandName')}
            </h1>
          </Link>
          <p className="text-gray-400">{t('auth.login.subtitle')}</p>
        </div>

        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-xl font-semibold text-center">
              {t('auth.login.title')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4 flex items-start">
                  <FaExclamationCircle className="text-red-500 mt-0.5 mr-2" />
                  <span className="text-red-500 text-sm">{error}</span>
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label htmlFor="username" className="block text-sm font-medium text-gray-300 mb-1">
                    {t('auth.login.form.username')}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaUser className="text-gray-500" />
                    </div>
                    <input
                      id="username"
                      type="text"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      className="block w-full pl-10 px-3 py-2 bg-gray-700 border border-gray-700 rounded-lg 
                               text-white placeholder-gray-400 focus:outline-none focus:border-indigo-500 
                               focus:ring-1 focus:ring-indigo-500"
                      placeholder={t('auth.login.form.usernamePlaceholder')}
                      required
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-1">
                    {t('auth.login.form.password')}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaLock className="text-gray-500" />
                    </div>
                    <input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="block w-full pl-10 px-3 py-2 bg-gray-700 border border-gray-700 rounded-lg 
                               text-white placeholder-gray-400 focus:outline-none focus:border-indigo-500 
                               focus:ring-1 focus:ring-indigo-500"
                      placeholder={t('auth.login.form.passwordPlaceholder')}
                      required
                    />
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading || isPending}
                className="w-full flex justify-center items-center px-4 py-2 border border-transparent 
                         rounded-lg shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 
                         focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 
                         disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              >
                {(isLoading || isPending) ? (
                  <>
                    <FaSpinner className="animate-spin mr-2" />
                    {t('auth.login.form.submitting')}
                  </>
                ) : (
                  t('auth.login.form.submit')
                )}
              </button>

              <div className="flex items-center justify-center mt-4">
                <div className="text-sm">
                  <Link 
                    to="/signup" 
                    className="font-medium text-indigo-400 hover:text-indigo-300 transition-colors duration-200"
                  >
                    {t('auth.login.links.signup')}
                  </Link>
                </div>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Login;